/**
 * API Client Utility - Authenticated Fetch Wrapper
 *
 * Provides a wrapper around fetch that:
 * - Includes credentials for Better Auth sessions
 * - Adds X-Tenant-ID header when tenantId is provided
 * - Handles 401 redirects
 * - Supports query parameters
 */

// NOTE: We intentionally DO NOT use NEXT_PUBLIC_API_URL here.
// Client-side requests must go through the Next.js proxy (via relative URLs)
// to ensure cookies are sent correctly (same-origin). The proxy is configured
// in next.config.js to forward /api/* requests to the backend.
const API_BASE_URL: string | undefined = undefined;

interface FetchOptions extends RequestInit {
  params?: Record<string, string>;
  tenantId?: string | null;
}

class APIError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
  ) {
    super(`API Error: ${status} ${statusText}`);
  }
}

async function authFetch<T = unknown>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { params, tenantId, headers = {}, ...restOptions } = options;

  // Build URL with query params
  // Support both absolute endpoints (when API_BASE_URL provided)
  // and relative endpoints (proxied via Next.js rewrites)
  let urlString: string;
  if (endpoint.startsWith('http')) {
    const url = new URL(endpoint);
    if (params) {
      Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
    }
    urlString = url.toString();
  } else if (API_BASE_URL) {
    const url = new URL(endpoint, API_BASE_URL);
    if (params) {
      Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
    }
    urlString = url.toString();
  } else {
    const url = new URL(endpoint, 'http://placeholder');
    if (params) {
      Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
    }
    // Strip placeholder origin to preserve relative path
    urlString = url.pathname + (url.search || '') + (url.hash || '');
  }

  try {
    const response = await fetch(urlString, {
      credentials: 'include', // Include cookies (Better Auth sessions)
      headers: {
        // Only set Content-Type for requests with a body (avoids Fastify empty JSON body error)
        ...(restOptions.body && { 'Content-Type': 'application/json' }),
        ...(tenantId && { 'X-Tenant-ID': tenantId }), // Add tenant header if available
        ...headers,
      },
      ...restOptions,
    });

    // Handle 401 - session expired, redirect to login
    if (response.status === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw new APIError(401, 'Unauthorized');
    }

    // Handle other error statuses
    if (!response.ok) {
      const errorData = (await response.json().catch(() => null)) as {
        message?: string;
        error?: string;
      } | null;
      throw new APIError(response.status, response.statusText, errorData);
    }

    // Parse JSON response
    if (response.headers.get('content-type')?.includes('application/json')) {
      return (await response.json()) as T;
    }

    // Non-JSON response, return empty object cast to T
    return {} as T;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new Error('Network error');
  }
}

// Convenience methods
authFetch.get = <T = unknown>(endpoint: string, options?: FetchOptions) =>
  authFetch<T>(endpoint, { ...options, method: 'GET' });

authFetch.post = <T = unknown>(endpoint: string, data?: unknown, options?: FetchOptions) =>
  authFetch<T>(endpoint, { ...options, method: 'POST', body: JSON.stringify(data) });

authFetch.put = <T = unknown>(endpoint: string, data?: unknown, options?: FetchOptions) =>
  authFetch<T>(endpoint, { ...options, method: 'PUT', body: JSON.stringify(data) });

authFetch.delete = <T = unknown>(endpoint: string, options?: FetchOptions) =>
  authFetch<T>(endpoint, { ...options, method: 'DELETE' });

authFetch.patch = <T = unknown>(endpoint: string, data?: unknown, options?: FetchOptions) =>
  authFetch<T>(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(data) });

export { authFetch };
